// KGOB Journal — shared visual + interaction components
// Ported from the standalone Vite source and adapted for React Router.

import React, { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ARTICLES, CATEGORIES, ARTICLE_BODIES } from './data';
import SubscribeModal from '@/components/subscriptions/SubscribeModal';

// ---------- Date utility: compute current journal issue from system clock ----------
const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV'];
const ISSUE_TITLES = {
  // Quarter (1-4) → editorial issue name
  1: 'The Winter Brief',
  2: 'The Spring Brief',
  3: 'The Summer Brief',
  4: 'The Year-End Brief',
};
export function getJournalIssue(d = new Date()) {
  const year = d.getFullYear();
  const month = d.getMonth(); // 0–11
  const quarter = Math.floor(month / 3) + 1;
  // Volume 1 = 2018 (founding year of The Journal). Volume IX = 2026.
  const volume = year - 2017;
  return {
    volume: ROMAN[volume - 1] || String(volume),
    issue: quarter,
    monthShort: d.toLocaleString('en-US', { month: 'short' }).toUpperCase(),
    monthLong: d.toLocaleString('en-US', { month: 'long' }).toUpperCase(),
    year,
    title: ISSUE_TITLES[quarter],
  };
}

// Only count essays that are actually published (have a body).
const PUBLISHED_ARTICLES = (ARTICLES || []).filter((a) => a.bodyKey && ARTICLE_BODIES[a.bodyKey]);
export const PUBLISHED_COUNT_BY_CATEGORY = (CATEGORIES || []).reduce((acc, c) => {
  acc[c] = c === 'All' ? PUBLISHED_ARTICLES.length : (PUBLISHED_ARTICLES || []).filter((a) => a.category === c).length;
  return acc;
}, {});

// ---------- Icons ----------
export const Icon = {
  linkedin: () => (
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zM8.34 18H5.67V9.66h2.67V18zM7 8.5a1.55 1.55 0 1 1 0-3.1 1.55 1.55 0 0 1 0 3.1zM18.34 18h-2.67v-4.06c0-.97-.02-2.21-1.35-2.21-1.36 0-1.57 1.06-1.57 2.15V18h-2.67V9.66h2.56v1.14h.04a2.8 2.8 0 0 1 2.52-1.38c2.7 0 3.2 1.78 3.2 4.09V18z"/></svg>
  ),
  reddit: () => (
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M22 12a2.2 2.2 0 0 0-3.74-1.58 10.9 10.9 0 0 0-5.84-1.84l1-4.68 3.27.7a1.57 1.57 0 1 0 .14-.66l-3.65-.78a.33.33 0 0 0-.39.25l-1.1 5.18a10.9 10.9 0 0 0-5.92 1.84A2.2 2.2 0 1 0 4.07 13a4.36 4.36 0 0 0-.05.6c0 3.06 3.57 5.55 7.97 5.55s7.97-2.49 7.97-5.55a4.36 4.36 0 0 0-.05-.6A2.2 2.2 0 0 0 22 12zM8 13.5a1.45 1.45 0 1 1 1.45 1.45A1.45 1.45 0 0 1 8 13.5zm6.6 3.4a3.5 3.5 0 0 1-2.6.95 3.5 3.5 0 0 1-2.6-.95.34.34 0 1 1 .48-.48 2.83 2.83 0 0 0 2.12.75 2.83 2.83 0 0 0 2.12-.75.34.34 0 0 1 .48.48zm-.05-1.95a1.45 1.45 0 1 1 1.45-1.45 1.45 1.45 0 0 1-1.45 1.45z"/></svg>
  ),
  x: () => (
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.2 2H21l-6.7 7.65L22 22h-6.18l-4.83-6.3L5.4 22H2.6l7.18-8.2L2 2h6.3l4.36 5.77L18.2 2zm-1.08 18.3h1.62L7 3.6H5.27L17.12 20.3z"/></svg>
  ),
  link: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.5 1.5"/><path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5"/></svg>
  ),
  mail: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="1"/><path d="M3 7l9 6 9-6"/></svg>
  ),
  bookmark: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"><path d="M6 4h12v17l-6-4-6 4V4z"/></svg>
  ),
  print: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"><path d="M6 9V3h12v6"/><rect x="3" y="9" width="18" height="9" rx="1"/><rect x="6" y="14" width="12" height="7"/></svg>
  ),
  audio: () => (
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 7v10l8-5z"/></svg>
  ),
  tldr: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><line x1="5" y1="7" x2="19" y2="7"/><line x1="5" y1="12" x2="14" y2="12"/><line x1="5" y1="17" x2="11" y2="17"/></svg>
  ),
  arrow: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
  ),
};

export function Avatar({ initials, size }) {
  return (
    <div
      className="avatar"
      style={size ? { width: size, height: size, fontSize: size * 0.42 } : null}
    >
      {initials}
    </div>
  );
}

export function LevelPill({ level }) {
  const n = level === 'Beginner' ? 1 : level === 'Intermediate' ? 2 : 3;
  return (
    <span className="level-pill">
      <span className="dots">
        {[0, 1, 2].map((i) => (
          <span key={i} className={'dot ' + (i < n ? 'on' : '')}></span>
        ))}
      </span>
      {level}
    </span>
  );
}

// ---------- Hero plate (topic-specific) ----------
export function HeroArt({ kind, mini, article }) {
  const plate = article?.heroPlate;
  const data = article?.heroData || {};
  const imageUrl = article?.image;

  let content = null;
  let bgClass = 'art art-plate';
  const hasImage = !!imageUrl;

  if (plate === 'section') {
    content = (
      <div className="plate-section">
        <div className="plate-section-symbol">§</div>
        <div className="plate-section-number">{data.section || '§174A'}</div>
        {data.subtitle && <div className="plate-eyebrow">{data.subtitle}</div>}
      </div>
    );
  } else if (plate === 'number') {
    content = (
      <div className="plate-number">
        <div className="plate-bignum">{data.number || ', '}</div>
        {data.caption && <div className="plate-caption">{data.caption}</div>}
        {data.detail && <div className="plate-detail">{data.detail}</div>}
      </div>
    );
  } else if (plate === 'state') {
    const states = Array.isArray(data.states) ? data.states : data.state ? [data.state] : ['NY'];
    content = (
      <div className="plate-state">
        <div className="plate-state-codes">
          {states.map((s, i) => (
            <React.Fragment key={s}>
              {i > 0 && <span className="plate-state-sep">·</span>}
              <span className="plate-state-code">{s}</span>
            </React.Fragment>
          ))}
        </div>
        {data.caption && <div className="plate-caption">{data.caption}</div>}
      </div>
    );
  } else if (plate === 'calendar') {
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const active = data.active || [2, 3, 4, 6];
    content = (
      <div className="plate-calendar">
        <div className="plate-eyebrow">Filing Year 2026</div>
        <div className="plate-cal-grid">
          {months.map((m, i) => (
            <div key={m} className={'plate-cal-cell ' + (active.includes(i) ? 'on' : '')}>
              {m}
            </div>
          ))}
        </div>
        <div className="plate-caption">{data.caption || '50 STATES · 50 DEADLINES'}</div>
      </div>
    );
  } else if (plate === 'states50') {
    const highlight = data.highlight || 41;
    content = (
      <div className="plate-states50">
        <div className="plate-eyebrow">{data.eyebrow || '50 STATES'}</div>
        <svg viewBox="0 0 500 250" className="plate-states50-svg">
          {Array.from({ length: 50 }).map((_, i) => {
            const row = Math.floor(i / 10);
            const col = i % 10;
            const cx = 40 + col * 47;
            const cy = 40 + row * 42;
            const on = i < highlight;
            return (
              <circle
                key={i}
                cx={cx}
                cy={cy}
                r={on ? 11 : 7}
                fill={on ? '#C49658' : 'rgba(196,150,88,0.18)'}
                stroke={on ? '#C49658' : 'rgba(196,150,88,0.3)'}
                strokeWidth="1"
              />
            );
          })}
        </svg>
        <div className="plate-caption">{data.caption || `${highlight} STATES WITH PTE ELECTION`}</div>
      </div>
    );
  } else if (plate === 'document') {
    content = (
      <div className="plate-document">
        <div className="plate-doc-form">
          <div className="plate-doc-header">
            <span>U.S. Department of the Treasury</span>
            <span className="plate-eyebrow" style={{ color: 'var(--brass)' }}>
              {data.formNum || 'Form'}
            </span>
          </div>
          <div className="plate-doc-title">{data.label || 'Form'}</div>
          <div className="plate-doc-sub">{data.sub || 'Information Return'}</div>
          <div className="plate-doc-rows">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="plate-doc-row">
                <span className="plate-doc-row-num">{String(i + 1).padStart(2, '0')}</span>
                <span
                  className="plate-doc-row-line"
                  style={{ width: 40 + Math.sin(i) * 30 + '%' }}
                ></span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  } else if (plate === 'timeline') {
    const points = data.points || [
      { y: '2019', label: 'SECURE 1.0' },
      { y: '2022', label: 'SECURE 2.0' },
      { y: '2024', label: 'Final Regs' },
      { y: '2025', label: 'Effective' },
    ];
    content = (
      <div className="plate-timeline">
        <div className="plate-eyebrow">{data.eyebrow || 'Legislative Timeline'}</div>
        <svg viewBox="0 0 500 140" className="plate-timeline-svg">
          <line x1="40" y1="80" x2="460" y2="80" stroke="rgba(196,150,88,0.4)" strokeWidth="1" />
          {points.map((p, i) => {
            const x = 40 + i * (420 / (points.length - 1));
            const active = i === points.length - 1;
            return (
              <g key={i}>
                <circle
                  cx={x}
                  cy="80"
                  r={active ? 8 : 5}
                  fill={active ? '#C49658' : 'transparent'}
                  stroke="#C49658"
                  strokeWidth="2"
                />
                <text x={x} y="50" textAnchor="middle" fill="#C49658" fontSize="14" fontFamily="JetBrains Mono, monospace">
                  {p.y}
                </text>
                <text
                  x={x}
                  y="110"
                  textAnchor="middle"
                  fill="rgba(246,242,234,0.8)"
                  fontSize="10"
                  fontFamily="Geist, sans-serif"
                  letterSpacing="0.08em"
                >
                  {p.label.toUpperCase()}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    );
  } else if (plate === 'chart') {
    content = (
      <div className="plate-chart-wrap">
        <div className="plate-eyebrow">{data.eyebrow || 'Trend over time'}</div>
        <svg viewBox="0 0 500 220" className="plate-chart-svg">
          <g stroke="rgba(196, 150, 88, 0.18)" strokeWidth="1" fill="none">
            {[40, 90, 140, 190].map((y) => (
              <line key={y} x1="0" y1={y} x2="500" y2={y} />
            ))}
          </g>
          <polyline
            points="20,180 70,160 120,165 170,130 220,110 270,75 320,55 380,40 440,30"
            fill="none"
            stroke="#C49658"
            strokeWidth="2.5"
          />
          <polyline
            points="20,200 70,190 120,180 170,165 220,160 270,140 320,120 380,105 440,95"
            fill="none"
            stroke="rgba(246, 242, 234, 0.5)"
            strokeWidth="1.5"
            strokeDasharray="4 4"
          />
          {[2021, 2022, 2023, 2024, 2025].map((y, i) => (
            <text
              key={y}
              x={20 + i * 110}
              y="215"
              fill="rgba(246,242,234,0.5)"
              fontSize="9"
              fontFamily="JetBrains Mono, monospace"
              letterSpacing="0.04em"
            >
              {y}
            </text>
          ))}
        </svg>
        {data.caption && <div className="plate-caption">{data.caption}</div>}
      </div>
    );
  } else {
    bgClass = 'art art-' + (kind || 'abstract');
    content = null;
  }

  return (
    <div className={mini ? 'art-mini' : ''}>
      {!mini && (
        <div className="feature-meta-strip">
          <span>PLATE 01 · KGOB JOURNAL</span>
          <span>
            {article?.year || '2025'} · {article?.category?.toUpperCase().slice(0, 3) || 'TAX'}
          </span>
        </div>
      )}
      <div className={bgClass} style={mini ? { position: 'absolute' } : null}>
        {hasImage && (
          <img
            src={imageUrl}
            alt=""
            className="plate-photo"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
        {hasImage && <div className="plate-photo-scrim" />}
        {content}
      </div>
    </div>
  );
}

// ---------- Share rail ----------
export function ShareRail({ article, shareCount }) {
  const [copied, setCopied] = useState(false);
  const url = 'https://kgob.com/journal/' + article.id;

  function copyLink() {
    if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }
  function share(network) {
    const u = encodeURIComponent(url);
    const title = encodeURIComponent(article.title);
    if (network === 'linkedin') {
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${u}`, '_blank', 'noopener');
    } else if (network === 'x') {
      window.open(`https://twitter.com/intent/tweet?text=${title}&url=${u}&via=kgobcpa`, '_blank', 'noopener');
    } else if (network === 'reddit') {
      window.open(`https://www.reddit.com/submit?url=${u}&title=${title}`, '_blank', 'noopener');
    } else if (network === 'email') {
      window.location.href = `mailto:?subject=${title}&body=${u}`;
    }
  }

  return (
    <div className="share-rail" data-testid="journal-article-share-rail">
      <span className="label">Share</span>
      <button className="share-btn" title="LinkedIn" onClick={() => share('linkedin')} data-testid="journal-share-linkedin">
        <Icon.linkedin />
      </button>
      <button className="share-btn" title="Reddit" onClick={() => share('reddit')} data-testid="journal-share-reddit">
        <Icon.reddit />
      </button>
      <button className="share-btn" title="X / Twitter" onClick={() => share('x')} data-testid="journal-share-x">
        <Icon.x />
      </button>
      <button className="share-btn" title="Email" onClick={() => share('email')} data-testid="journal-share-email">
        <Icon.mail />
      </button>
      <button
        className={'share-btn ' + (copied ? 'copied' : '')}
        title={copied ? 'Copied' : 'Copy link'}
        onClick={copyLink}
        data-testid="journal-share-copy-link"
      >
        {copied ? (
          <span style={{ fontSize: 10, fontFamily: 'var(--ui)', letterSpacing: '0.1em' }}>✓</span>
        ) : (
          <Icon.link />
        )}
      </button>
      <div className="share-count">
        {shareCount}
        <br />
        shares
      </div>
    </div>
  );
}

// ---------- Top mast ----------
export function Masthead() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const path = location.pathname;
  const isIndex = path === '/journal' || path === '/journal/';
  const isArchive = path === '/journal/archive';
  // Active category from URL, only meaningful on the index page.
  const activeCat = isIndex ? (searchParams.get('cat') || 'All') : null;
  // Auto-detected issue from current date.
  const issue = getJournalIssue();

  // Category pill helper — driven by data, with essay counts and active state.
  const subnavCats = [
    { key: 'Latest', cat: 'All', onClick: () => navigate('/journal') },
    { key: 'Tax Strategy', cat: 'Tax Strategy', onClick: () => navigate('/journal?cat=Tax%20Strategy') },
    { key: 'Wealth Planning', cat: 'Wealth Planning', onClick: () => navigate('/journal?cat=Wealth%20Planning') },
    { key: 'Tax Controversy', cat: 'Tax Controversy', onClick: () => navigate('/journal?cat=Tax%20Controversy') },
    { key: 'Compliance', cat: 'Compliance', onClick: () => navigate('/journal?cat=Compliance') },
    { key: 'State & Local', cat: 'State & Local', onClick: () => navigate('/journal?cat=State%20%26%20Local') },
  ];

  return (
    <>
      <SubscribeModal
        open={subscribeOpen}
        onClose={() => setSubscribeOpen(false)}
        listId="journal"
        source="journal_masthead"
      />
      <header className="masthead" data-testid="journal-masthead">
        <div className="masthead-inner">
          <div className="mast-left">
            <span className="mast-link" onClick={() => navigate('/journal')} data-testid="journal-nav-home">
              The Journal
            </span>
            <span className="mast-link" onClick={() => navigate('/journal/archive')} data-testid="journal-nav-archive">
              Archive
            </span>
            <span data-testid="components-span-2" className="mast-link" onClick={() => navigate('/services')}>
              Practices
            </span>
            <span className="mast-link" onClick={() => setSubscribeOpen(true)} data-testid="journal-mast-newsletter">Newsletter</span>
          </div>
          <div
            className="wordmark"
            onClick={() => navigate('/journal')}
            style={{ cursor: 'pointer' }}
            data-testid="journal-wordmark"
          >
            <img src="/brand/kgob-logo-light.svg" alt="Kohari Gonzalez Oneyear & Brown CPAs & Advisors" className="wordmark-mark" />
            <div className="wordmark-text">
              <span className="wordmark-main">The Journal</span>
            </div>
          </div>
          <div className="mast-right">
            <span data-testid="components-span-1" className="mast-link" onClick={() => navigate('/journal/archive')}>Search</span>
            <span className="mast-link" onClick={() => setSubscribeOpen(true)} data-testid="journal-mast-subscribe">Subscribe</span>
            <a
              className="mast-link"
              href="/"
              data-testid="journal-back-to-home"
            >
              Home
            </a>
            <a
              className="mast-link"
              href="https://app.kgob.com/portal/login"
              target="_blank"
              rel="noopener noreferrer"
              style={{ borderBottom: '1px solid var(--ink)' }}
              data-testid="journal-client-portal"
            >
              Client Portal
            </a>
          </div>
        </div>
      </header>
      <div className="subnav">
        <div className="subnav-inner">
          <div className="subnav-left">
            {subnavCats.map(({ key, cat, onClick }) => {
              const count = PUBLISHED_COUNT_BY_CATEGORY[cat] || 0;
              const isActive = isIndex && activeCat === cat;
              return (
                <span
                  key={key}
                  className={'subnav-pill ' + (isActive ? 'active' : '')}
                  onClick={onClick}
                  data-testid={`journal-subnav-${key.replace(/\s+/g, '-').replace(/&/g, 'and').toLowerCase()}`}
                  title={`${count} essays in ${key}`}
                >
                  {key}
                  <span
                    className="mono"
                    style={{
                      marginLeft: 6,
                      fontSize: 10,
                      color: 'var(--brass)',
                      letterSpacing: '0.04em',
                      opacity: isActive ? 1 : 0.7,
                    }}
                  >
                    {String(count).padStart(2, '0')}
                  </span>
                </span>
              );
            })}
            <span
              className={'subnav-pill ' + (isArchive ? 'active' : '')}
              onClick={() => navigate('/journal/archive')}
              data-testid="journal-subnav-archive"
            >
              Archive
            </span>
          </div>
          <div className="subnav-right">
            <span className="mono">VOL. {issue.volume} · NO. {issue.issue} · {issue.monthShort} {issue.year}</span>
          </div>
        </div>
      </div>
    </>
  );
}

export function Colophon() {
  const navigate = useNavigate();
  return (
    <>
      <div className="colophon" data-testid="journal-colophon">
        <div className="col-1">
          <img
            src="/brand/kgob-logo-light.svg"
            alt="Kohari Gonzalez Oneyear & Brown CPAs & Advisors"
            className="colophon-logo"
          />
          <p
            style={{
              fontFamily: 'var(--serif)',
              fontStyle: 'italic',
              fontSize: 14,
              color: 'var(--ink-soft)',
              marginTop: 18,
              maxWidth: 320,
            }}
          >
            Luxury Advisory. Precision Compliance. Quarterly correspondence from our partners on tax, structure, and stewardship.
          </p>
        </div>
        <div>
          <h4>The Journal</h4>
          <a data-testid="components-a-15" onClick={() => navigate('/journal')} style={{ cursor: 'pointer' }}>Latest</a>
          <a data-testid="components-a-14" onClick={() => navigate('/journal/archive')} style={{ cursor: 'pointer' }}>Archive</a>
          <a data-testid="components-a-13" onClick={() => navigate('/journal')} style={{ cursor: 'pointer' }}>Topics</a>
          <a data-testid="components-a-12" onClick={() => navigate('/journal')} style={{ cursor: 'pointer' }}>Authors</a>
          <a data-testid="components-a-11" onClick={() => navigate('/journal')} style={{ cursor: 'pointer' }}>Citation Guide</a>
        </div>
        <div>
          <h4>The Firm</h4>
          <a data-testid="components-a-10" onClick={() => navigate('/our-story')} style={{ cursor: 'pointer' }}>About KGOB</a>
          <a data-testid="components-a-9" onClick={() => navigate('/services')} style={{ cursor: 'pointer' }}>Practices</a>
          <a data-testid="components-a-8" onClick={() => navigate('/journal')} style={{ cursor: 'pointer' }}>Insights</a>
          <a data-testid="components-a-7" onClick={() => navigate('/contact')} style={{ cursor: 'pointer' }}>Careers</a>
          <a data-testid="components-a-6" onClick={() => navigate('/contact')} style={{ cursor: 'pointer' }}>Contact</a>
        </div>
        <div>
          <h4>Follow</h4>
          <a data-testid="components-a-5" href="https://www.linkedin.com/company/kgob" target="_blank" rel="noopener noreferrer">LinkedIn</a>
          <a data-testid="components-a-4" href="https://twitter.com/kgobcpa" target="_blank" rel="noopener noreferrer">X · @kgobcpa</a>
          <a data-testid="components-a-3" href="https://instagram.com/kgobcpa" target="_blank" rel="noopener noreferrer">Instagram · @kgobcpa</a>
          <a data-testid="components-a-2" onClick={() => navigate('/journal')} style={{ cursor: 'pointer' }}>RSS feed</a>
          <a data-testid="components-a-1" onClick={() => navigate('/journal')} style={{ cursor: 'pointer' }}>Republish guidelines</a>
        </div>
      </div>
      <div className="colophon-bottom">
        <span>&copy; {new Date().getFullYear()} Kohari Gonzalez Oneyear &amp; Brown PLLC &middot; All rights reserved.</span>
        <span>This publication is provided for informational purposes only and does not constitute tax or legal advice.</span>
      </div>
    </>
  );
}
